import { useLayoutEffect, useRef, type RefObject } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/** Enough history entries for any realistic Back chain; older positions are dropped. */
const MAX_REMEMBERED = 100;

/**
 * Scroll restoration for the layout's own scroller. The app scrolls inside `<main>`, not the
 * window, so neither the browser nor React Router resets it between routes.
 *
 * A new page (link, `navigate`) opens at the top. Back and Forward return to where that history
 * entry was left. A change to the query string alone (a filter, an open row) keeps the position.
 * Restoring is best-effort: a page whose content is still loading clamps to what has rendered.
 * Call the returned function from the scroller's `onScroll` with its `scrollTop`.
 */
export function useScrollerRestoration(scrollerRef: RefObject<HTMLElement | null>) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const positionsRef = useRef(new Map<string, number>());
  const lastPathnameRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    // Only a new path moves the scroller: a query-string change keeps the reader where they are.
    if (lastPathnameRef.current === location.pathname) return;
    lastPathnameRef.current = location.pathname;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const saved = navigationType === 'POP' ? positionsRef.current.get(location.key) : undefined;
    scroller.scrollTop = saved ?? 0;
  }, [scrollerRef, location.pathname, location.key, navigationType]);

  return (scrollTop: number) => {
    const map = positionsRef.current;
    map.delete(location.key);
    map.set(location.key, scrollTop);
    if (map.size > MAX_REMEMBERED) {
      const oldest = map.keys().next().value;
      if (oldest !== undefined) map.delete(oldest);
    }
  };
}
